import { SeatingStyle, SeatingStyleModule_fromInt, TableShape, TableShapeModule_fromInt } from "../Shared/Types.js";
import { min, max } from "../fable_modules/fable-library-js.4.28.0/Double.js";
import { tryFind, ofArray, indexed, item as item_1, fold, sum, tail, singleton, append, head, max as max_1, sumBy, sortBy, map, empty, isEmpty } from "../fable_modules/fable-library-js.4.28.0/List.js";
import { equals, createObj, comparePrimitives, compareArrays } from "../fable_modules/fable-library-js.4.28.0/Util.js";
import { createElement } from "react";
import { reactApi } from "../fable_modules/Feliz.2.9.0/Interop.fs.js";
import { item as item_2 } from "../fable_modules/fable-library-js.4.28.0/Array.js";
import { Msg } from "../App/Messages.js";
import { collect, map as map_1, singleton as singleton_1, append as append_1, delay, toList } from "../fable_modules/fable-library-js.4.28.0/Seq.js";
import { rangeDouble } from "../fable_modules/fable-library-js.4.28.0/Range.js";
import { defaultOf } from "../fable_modules/fable-library-js.4.28.0/Util.js";
import { t as t_4 } from "../Lang.js";
import { Floor_noTables, Floor_title, Floor_hint, Floor_gridSnap, Floor_autoLayout, Floor_rotateCwTitle, Floor_rotateCw, Floor_rotateCcwTitle, Floor_rotateCcw } from "../Shared/Tr.js";
import { helpIcon } from "./Components.js";

const canvasWidth = 1200;

const canvasHeight = 800;

/**
 * Table dimensions by shape, using L/H for non-round tables.
 * Sizes are ~2x the original to allow room for guest name labels.
 */
export function tableDimensions(table) {
    const capacity = table.Capacity | 0;
    const H = table.SeatsShortEnd | 0;
    const matchValue = TableShapeModule_fromInt(table.Shape);
    switch (matchValue.tag) {
        case 3: {
            const baseSize_1 = max(4, capacity) * 15;
            return [baseSize_1, baseSize_1];
        }
        case 1: {
            const width = (max(4, table.SeatsLongSide) * 30) + (H * 20);
            const height = max(2, (H > 0) ? H : 2) * 36;
            return [max(width, 140), max(height, 72)];
        }
        case 2: {
            const baseSize_2 = max(4, capacity) * 15;
            return [baseSize_2 * 1.4, baseSize_2 * 0.8];
        }
        default: {
            const baseSize = max(4, capacity) * 15;
            return [baseSize, baseSize];
        }
    }
}

/**
 * Compute row-packed positions for tables on a canvas.
 * Returns a list of (tableId, x, y) tuples for each table.
 */
export function TableLayoutHelper_computeRowPackedPositions(tables, canvasW, canvasH, padding) {
    if (isEmpty(tables)) {
        return empty();
    }
    else {
        const boxes = map((t_1) => {
            const patternInput = tableDimensions(t_1);
            return [t_1, patternInput[0] + padding, patternInput[1] + padding];
        }, sortBy((t) => [t.IsHonorTable ? 0 : 1, t.SortOrder], tables, {
            Compare: compareArrays,
        }));
        const totalArea = sumBy((tupledArg) => (tupledArg[1] * tupledArg[2]), boxes, {
            GetZero: () => 0,
            Add: (x_1, y_1) => (x_1 + y_1),
        });
        const targetWidth = min(canvasW, max(max_1(map((tupledArg_1) => tupledArg_1[1], boxes), {
            Compare: comparePrimitives,
        }), Math.sqrt(totalArea * 1.3)));
        const packRows = (remaining_mut, rows_mut, currentRow_mut, rowX_mut, rowMaxH_mut) => {
            packRows:
            while (true) {
                const remaining = remaining_mut, rows = rows_mut, currentRow = currentRow_mut, rowX = rowX_mut, rowMaxH = rowMaxH_mut;
                if (!isEmpty(remaining)) {
                    const bw = head(remaining)[1];
                    const bh = head(remaining)[2];
                    if (((rowX + bw) > targetWidth) && !isEmpty(currentRow)) {
                        remaining_mut = remaining;
                        rows_mut = append(rows, singleton(currentRow));
                        currentRow_mut = empty();
                        rowX_mut = 0;
                        rowMaxH_mut = 0;
                        continue packRows;
                    }
                    else {
                        const item = [head(remaining)[0], rowX + (bw / 2), bh / 2, bw, bh];
                        const newRowMaxH = max(rowMaxH, bh);
                        remaining_mut = tail(remaining);
                        rows_mut = rows;
                        currentRow_mut = append(currentRow, singleton(item));
                        rowX_mut = (rowX + bw);
                        rowMaxH_mut = newRowMaxH;
                        continue packRows;
                    }
                }
                else if (isEmpty(currentRow)) {
                    return rows;
                }
                else {
                    return append(rows, singleton(currentRow));
                }
                break;
            }
        };
        const rows_1 = packRows(boxes, empty(), empty(), 0, 0);
        const rowHeights = map((row) => max_1(map((tupledArg_2) => tupledArg_2[4], row), {
            Compare: comparePrimitives,
        }), rows_1);
        const totalBlockHeight = sum(rowHeights, {
            GetZero: () => 0,
            Add: (x_4, y_4) => (x_4 + y_4),
        });
        const totalBlockWidth = max_1(map((row_1) => sumBy((tupledArg_3) => tupledArg_3[3], row_1, {
            GetZero: () => 0,
            Add: (x_5, y_5) => (x_5 + y_5),
        }), rows_1), {
            Compare: comparePrimitives,
        });
        const offsetX = max(padding, (canvasW - totalBlockWidth) / 2);
        return fold((tupledArg_4, tupledArg_5) => {
            const curY = tupledArg_4[0];
            const row_2 = tupledArg_5[1];
            const rowH = item_1(tupledArg_5[0], rowHeights);
            const rowOffsetX = offsetX + ((totalBlockWidth - sumBy((tupledArg_6) => tupledArg_6[3], row_2, {
                GetZero: () => 0,
                Add: (x_7, y_7) => (x_7 + y_7),
            })) / 2);
            return [curY + rowH, append(tupledArg_4[1], map((tupledArg_7) => [tupledArg_7[0].Id, rowOffsetX + tupledArg_7[1], curY + (rowH / 2)], row_2))];
        }, [max(padding, (canvasH - totalBlockHeight) / 2), empty()], indexed(rows_1))[1];
    }
}

function tableShapeClass(isHonor, isSelected) {
    if (isSelected) {
        return "floor-table-selected";
    }
    else if (isHonor) {
        return "floor-table-honor";
    }
    else {
        return "floor-table";
    }
}

function renderTableShape(table, isSelected, isDragging, dispatch) {
    let elements, elements_1, elements_2;
    const patternInput = tableDimensions(table);
    const w = patternInput[0];
    const h = patternInput[1];
    const cx = table.PositionX;
    const cy = table.PositionY;
    const shapeClass = tableShapeClass(table.IsHonorTable, isSelected);
    const cursorClass = isDragging ? "floor-plan-table dragging" : "floor-plan-table";
    let shapeEl;
    const matchValue = TableShapeModule_fromInt(table.Shape);
    shapeEl = ((matchValue.tag === 3) ? createElement("g", createObj(ofArray([["transform", `rotate(${table.Rotation} ${cx} ${cy})`], (elements = singleton(createElement("rect", {
        x: cx - (w / 2),
        y: cy - (h / 2),
        width: w,
        height: h,
        className: shapeClass,
        rx: "4",
    })), ["children", reactApi.Children.toArray(Array.from(elements))])]))) : ((matchValue.tag === 1) ? createElement("g", createObj(ofArray([["transform", `rotate(${table.Rotation} ${cx} ${cy})`], (elements_1 = singleton(createElement("rect", {
        x: cx - (w / 2),
        y: cy - (h / 2),
        width: w,
        height: h,
        className: shapeClass,
        rx: "6",
    })), ["children", reactApi.Children.toArray(Array.from(elements_1))])]))) : ((matchValue.tag === 2) ? createElement("g", createObj(ofArray([["transform", `rotate(${table.Rotation} ${cx} ${cy})`], (elements_2 = singleton(createElement("ellipse", {
        cx: cx,
        cy: cy,
        rx: (w / 2).toString(),
        ry: (h / 2).toString(),
        className: shapeClass,
    })), ["children", reactApi.Children.toArray(Array.from(elements_2))])]))) : createElement("circle", {
        cx: cx,
        cy: cy,
        r: w / 2,
        className: shapeClass,
    }))));
    const hitArea = createElement("rect", {
        x: (cx - (w / 2)) - 5,
        y: (cy - (h / 2)) - 5,
        width: w + 10,
        height: h + 10,
        fill: "transparent",
        className: cursorClass,
        onMouseDown: (e) => {
            e.preventDefault();
            const coords = (function(evt, cw) { var svg = evt.currentTarget.closest ? evt.currentTarget.closest('svg') : evt.currentTarget; if (!svg) return [0,0]; var rect = svg.getBoundingClientRect(); var scale = cw / rect.width; return [(evt.clientX - rect.left) * scale, (evt.clientY - rect.top) * scale]; })(e, canvasWidth);
            dispatch(new Msg(207, [table.Id, item_2(0, coords) - cx, item_2(1, coords) - cy]));
        },
    });
    const nameLabel = createElement("text", {
        x: cx,
        y: cy - 6,
        textAnchor: "middle",
        dominantBaseline: "auto",
        className: "floor-table-name",
        pointerEvents: "none",
        children: table.Name,
    });
    const capacityLabel = createElement("text", createObj(toList(delay(() => append_1(singleton_1(["x", cx]), delay(() => append_1(singleton_1(["y", cy + 14]), delay(() => append_1(singleton_1(["textAnchor", "middle"]), delay(() => append_1(singleton_1(["dominantBaseline", "auto"]), delay(() => append_1(singleton_1(["className", "floor-table-capacity"]), delay(() => append_1(singleton_1(["pointerEvents", "none"]), delay(() => singleton_1(["children", (equals(TableShapeModule_fromInt(table.Shape), new TableShape(0, [])) ? true : equals(SeatingStyleModule_fromInt(table.SeatingStyle), new SeatingStyle(1, []))) ? (`(${table.Capacity} seats)`) : ((table.SeatsShortEnd > 0) ? (`(${table.SeatsLongSide}×${table.SeatsShortEnd} = ${table.Capacity})`) : (`(${table.SeatsLongSide}×2 = ${table.Capacity})`))])))))))))))))))));
    return createElement("g", {
        children: reactApi.Children.toArray([hitArea, shapeEl, nameLabel, capacityLabel]),
    });
}

const gridBackground = createElement("g", createObj(singleton((() => {
    const elements = toList(delay(() => append_1(map_1((x) => createElement("line", {
        x1: x,
        y1: 0,
        x2: x,
        y2: canvasHeight,
        className: "floor-grid-line",
    }), rangeDouble(0, 50, canvasWidth)), delay(() => map_1((y) => createElement("line", {
        x1: 0,
        y1: y,
        x2: canvasWidth,
        y2: y,
        className: "floor-grid-line",
    }), rangeDouble(0, 50, canvasHeight))))));
    return ["children", reactApi.Children.toArray(Array.from(elements))];
})())));

function rotationControls(state, dispatch) {
    let elems;
    const matchValue = state.SelectedFloorPlanTable;
    if (matchValue == null) {
        return defaultOf();
    }
    else {
        const tableId = matchValue;
        const matchValue_1 = tryFind((t) => (t.Id === tableId), state.Tables);
        if (matchValue_1 == null) {
            return defaultOf();
        }
        else {
            const table = matchValue_1;
            const matchValue_2 = TableShapeModule_fromInt(table.Shape);
            switch (matchValue_2.tag) {
                case 0:
                    return defaultOf();
                default:
                    return createElement("span", createObj(ofArray([["className", "floor-plan-rotate-group"], (elems = [createElement("button", {
                        className: "btn btn-sm btn-secondary",
                        children: t_4(Floor_rotateCcw),
                        title: t_4(Floor_rotateCcwTitle),
                        onClick: (_arg) => {
                            dispatch(new Msg(210, [tableId, table.Rotation - 15]));
                        },
                    }), createElement("span", {
                        className: "floor-plan-rotation-display",
                        children: `${~~table.Rotation} deg`,
                    }), createElement("button", {
                        className: "btn btn-sm btn-secondary",
                        children: t_4(Floor_rotateCw),
                        title: t_4(Floor_rotateCwTitle),
                        onClick: (_arg_1) => {
                            dispatch(new Msg(210, [tableId, table.Rotation + 15]));
                        },
                    })], ["children", reactApi.Children.toArray(Array.from(elems))])])));
            }
        }
    }
}

function toolbar(state, dispatch) {
    let elems_1, elems;
    return createElement("div", createObj(ofArray([["className", "floor-plan-toolbar"], (elems_1 = [createElement("button", {
        className: "btn btn-secondary btn-sm",
        children: t_4(Floor_autoLayout),
        onClick: (_arg) => {
            dispatch(new Msg(212, []));
        },
    }), createElement("label", createObj(ofArray([["className", "floor-plan-snap-label"], (elems = [createElement("input", {
        type: "checkbox",
        checked: state.GridSnap,
        onChange: (ev) => {
            const _arg_1 = ev.target.checked;
            dispatch(new Msg(213, []));
        },
    }), createElement("span", {
        children: t_4(Floor_gridSnap),
    })], ["children", reactApi.Children.toArray(Array.from(elems))])]))), rotationControls(state, dispatch), createElement("span", {
        className: "text-muted text-sm",
        style: {
            marginLeft: 16,
        },
        children: t_4(Floor_hint),
    })], ["children", reactApi.Children.toArray(Array.from(elems_1))])])));
}

/**
 * Main floor plan editor view
 */
export function view(state, dispatch) {
    let elems_4;
    return createElement("div", createObj(ofArray([["className", "floor-plan-editor"], (elems_4 = toList(delay(() => {
        let elems;
        return append_1(singleton_1(createElement("div", createObj(ofArray([["className", "flex items-center gap-sm mb-2"], (elems = [createElement("h3", {
            children: t_4(Floor_title),
        }), helpIcon(() => {
            dispatch(new Msg(268, ["03", "floor-plan-editor"]));
        })], ["children", reactApi.Children.toArray(Array.from(elems))])])))), delay(() => {
            let elems_1;
            return isEmpty(state.Tables) ? singleton_1(createElement("div", createObj(ofArray([["className", "card"], (elems_1 = [createElement("p", {
                className: "text-muted",
                children: t_4(Floor_noTables),
            })], ["children", reactApi.Children.toArray(Array.from(elems_1))])])))) : append_1(singleton_1(toolbar(state, dispatch)), delay(() => {
                let elems_3, elements;
                return singleton_1(createElement("div", createObj(ofArray([["className", "floor-plan-canvas-container"], (elems_3 = [createElement("svg", createObj(ofArray([["className", "floor-plan-svg"], ["viewBox", (((((0 + " ") + 0) + " ") + ~~canvasWidth) + " ") + ~~canvasHeight], ["preserveAspectRatio", "xMidYMid meet"], ["onMouseMove", (e) => {
                    if (state.IsDraggingTable == null) {
                    }
                    else {
                        e.preventDefault();
                        const coords = (function(evt, cw) { var svg = evt.currentTarget.closest ? evt.currentTarget.closest('svg') : evt.currentTarget; if (!svg) return [0,0]; var rect = svg.getBoundingClientRect(); var scale = cw / rect.width; return [(evt.clientX - rect.left) * scale, (evt.clientY - rect.top) * scale]; })(e, canvasWidth);
                        dispatch(new Msg(208, [item_2(0, coords), item_2(1, coords)]));
                    }
                }], ["onMouseUp", (_e) => {
                    if (state.IsDraggingTable == null) {
                    }
                    else {
                        dispatch(new Msg(209, []));
                    }
                }], ["onMouseLeave", (_e_1) => {
                    if (state.IsDraggingTable == null) {
                    }
                    else {
                        dispatch(new Msg(209, []));
                    }
                }], (elements = toList(delay(() => append_1(singleton_1(createElement("rect", {
                    x: 0,
                    y: 0,
                    width: canvasWidth,
                    height: canvasHeight,
                    className: "floor-canvas-bg",
                })), delay(() => append_1(singleton_1(gridBackground), delay(() => collect((table) => singleton_1(renderTableShape(table, equals(state.SelectedFloorPlanTable, table.Id), equals(state.IsDraggingTable, table.Id), dispatch)), state.Tables))))))), ["children", reactApi.Children.toArray(Array.from(elements))])])))], ["children", reactApi.Children.toArray(Array.from(elems_3))])]))));
            }));
        }));
    })), ["children", reactApi.Children.toArray(Array.from(elems_4))])])));
}

